import { useEffect, useRef, useState } from 'react';

import type { DrawerNode } from '../drawer.types';

type UseNestedLevelProps = {
    rootIsOpen: boolean;
    onBack: () => void;
    onCloseAll: () => void;
};

export function useNestedLevel({ rootIsOpen, onBack, onCloseAll }: UseNestedLevelProps) {
    const [activeNode, setActiveNode] = useState<DrawerNode | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const activeTriggerRef = useRef<HTMLElement | null>(null);
    const isBackNavigationRef = useRef(false);

    useEffect(() => setIsOpen(rootIsOpen), [rootIsOpen]);

    const openNode = (nextNode: DrawerNode, trigger: HTMLElement) => {
        activeTriggerRef.current = trigger;
        setActiveNode(nextNode);
    };

    const closeChild = () => {
        setActiveNode(null);
        requestAnimationFrame(() => activeTriggerRef.current?.focus());
    };

    const handleBack = () => {
        isBackNavigationRef.current = true;
        setIsOpen(false);
    };

    const handleOpenChange = (nextIsOpen: boolean) => {
        setIsOpen(nextIsOpen);

        if (!nextIsOpen && !isBackNavigationRef.current) onCloseAll();
    };

    const handleOpenChangeComplete = (isNowOpen: boolean) => {
        if (isNowOpen) return;

        if (isBackNavigationRef.current) {
            isBackNavigationRef.current = false;
            onBack();
        }
    };

    return {
        activeNode,
        closeChild,
        handleBack,
        handleOpenChange,
        handleOpenChangeComplete,
        isOpen,
        openNode,
    };
}
